import type { CaseStudySection } from './amexCaseStudy';
export const rsiIntro = {
  client: 'RSI',
  title: 'Modernizing platform architecture to improve profitability by 46%',
  summary: 'Redesigned the core architecture of a patient communications SaaS product supporting 2,000+ providers, cutting support volume and shortening time-to-value.',
  background: 'linear-gradient(180deg, #F4E6E4 0%, #D7C5C1 100%)'
};
export const rsiMetrics = [{
  value: '54→79%',
  label: 'gross profit margin'
}, {
  value: '22→28%',
  label: 'conversion'
}, {
  value: '$1.5M',
  label: 'contract value'
}];
export const rsiSections: CaseStudySection[] = [{
  label: 'Problem',
  paragraphs: ['RSI’s patient communications platform was hard to learn, navigate, and set up. Onboarding took up to 12 weeks, and customers had to call support for simple admin tasks. The dated UI also made the product look more complicated than it was, which slowed sales. The bet was that a better UX would cut support costs and onboarding time and make the product more appealing to buyers.']
}, {
  label: 'Challenge',
  paragraphs: ['The product was 150+ screens built by a series of developers over several years. It sat on a complex, idiosyncratic data structure inherited from the EHR. It also had to serve two audiences with different needs: daily users handling patient communications, and managers who read reports and adjust configuration.']
}, {
  label: 'Key Decisions',
  bullets: ['Created a unified inbox for all communications, so staff see a patient’s full history with the practice at a glance and can respond faster.', 'Reorganized navigation around primary use cases, putting the most-used features within easy reach to shorten the learning curve. I introduced a rail navigation to separate the major sections, then redesigned each section in phases to speed rollout.', 'Standardized data management across the product, so all practice data lives in one place and looks the same everywhere, which simplifies onboarding.', 'Started from Material Design because it was compatible with Angular, and prototyped the full product in Figma, testing it with users before launch.']
}, {
  label: 'Results',
  paragraphs: ['Profit margin rose from 54% to 79% (a 46% relative increase) because of lower support and onboarding costs. For one major client, lead-to-scheduled conversion went from 22% to 28%. The redesign let RSI move upmarket into accounts worth up to $1.5M a year per contract. Feedback from clients and partners was universally positive.']
}, {
  figures: [{
    src: 'https://cdn.magicpatterns.com/uploads/i153t7huV5A4wxqJTJxwUg/inbox.jpg',
    alt: 'RSI unified inbox with filters by message type, assignee, location, and tag, a conversation list, and an open patient thread with attached consent forms',
    caption: 'Universal inbox for phone, SMS, email, fax, and more.'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/iHjDcWcLoBVtoZCTjsVMgS/kanban.jpg',
    alt: 'CRM board view of leads organized into columns from Lead Created through Closed',
    caption: 'Kanban view of leads moving from first contact to close.'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/pBQZ6t5TCku63QLKEMCyB7/audiences.jpg',
    alt: 'Create Audience screen with a filter list, configured birthday and appointment filters, and a live preview list of matching contacts',
    caption: 'Audience builder with live preview of matching contacts.'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/jTcEhe5F3RjL1VjsHLr86N/reports.jpg',
    alt: 'Patient Acquisition report with headline KPIs, a monthly leads trend chart, leads by campaign, and leads by source',
    caption: 'Patient acquisition reporting at a glance.'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/4tQ8eBvSpdrx2cjxAjxFwA/provider.jpg',
    alt: 'Edit Provider panel separating synced EHR fields from editable platform settings and provider details',
    caption: 'Provider settings, with EHR-synced fields clearly separated.'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/4SxerV9Y31TnMcZUMo3LFC/widget.jpg',
    alt: 'Embed Scheduling Widget settings with configuration options and a live phone-sized preview of the booking flow',
    caption: 'Scheduling widget setup with a live preview.'
  }]
}];